import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Card, CardBody, Button, Chip, Skeleton } from '@nextui-org/react';
import {
  Users,
  User,
  Gamepad2,
  ArrowRight,
  ImageOff,
  MessagesSquare,
  MessageCircle,
  Flame,
  Sparkles,
  UserPlus,
  LogIn,
  ArrowBigUp,
  Clock,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import communityService, {
  ProfilePreview,
  PublicDartSetup,
} from '../services/communityService';
import forumService, { ForumTopic } from '../services/forumService';
import { useAuth } from '../contexts/AuthContext';

const ACCENT = '#3fced1';

const CommunityPage: React.FC = () => {
  const { isAuthenticated } = useAuth();

  const [profiles, setProfiles] = useState<ProfilePreview[]>([]);
  const [setups, setSetups] = useState<PublicDartSetup[]>([]);
  const [forumTopics, setForumTopics] = useState<ForumTopic[]>([]);
  const [totalProfiles, setTotalProfiles] = useState<number | null>(null);
  const [totalSetups, setTotalSetups] = useState<number | null>(null);
  const [totalTopics, setTotalTopics] = useState<number | null>(null);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [loadingSetups, setLoadingSetups] = useState(true);
  const [loadingTopics, setLoadingTopics] = useState(true);

  useEffect(() => {
    let active = true;

    const loadProfiles = async () => {
      setLoadingProfiles(true);
      const res = await communityService.getRecentProfiles({ limit: 8 });
      if (!active) return;
      setProfiles(res.profiles);
      setTotalProfiles(typeof res.total === 'number' && res.total > 0 ? res.total : null);
      setLoadingProfiles(false);
    };

    const loadSetups = async () => {
      setLoadingSetups(true);
      const res = await communityService.getAllSetups({ limit: 8 });
      if (!active) return;
      setSetups(res.setups);
      setTotalSetups(typeof res.total === 'number' && res.total > 0 ? res.total : null);
      setLoadingSetups(false);
    };

    const loadForumTopics = async () => {
      setLoadingTopics(true);
      try {
        const res = await forumService.getTopics({ limit: 5, sort: 'hot' });
        if (!active) return;
        setForumTopics(Array.isArray(res.topics) ? res.topics : []);
        setTotalTopics(typeof res.total === 'number' && res.total > 0 ? res.total : null);
      } catch (error) {
        // Defensiv: Bei Fehler wird die Forum-Sektion komplett ausgeblendet
        if (!active) return;
        setForumTopics([]);
        setTotalTopics(null);
      } finally {
        if (active) setLoadingTopics(false);
      }
    };

    loadProfiles();
    loadSetups();
    loadForumTopics();

    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Community - Spielerprofile & Dart-Setups | DartCloud</title>
        <meta
          name="description"
          content="Entdecke die DartCloud Community: öffentliche Spielerprofile und Dart-Setups anderer Spieler. Lass dich inspirieren und finde Gleichgesinnte."
        />
        <meta
          name="keywords"
          content="dart community, spielerprofile, dart setups, dartcloud, dart spieler"
        />
      </Helmet>

      <div className="min-h-screen bg-zinc-950">
        {/* ============ Hero ============ */}
        <section className="relative overflow-hidden border-b border-zinc-800/80">
          {/* Glow / Gradient-Hintergrund */}
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 via-zinc-950 to-zinc-950" />
          <div
            className="absolute -top-32 left-1/2 -translate-x-1/2 w-[640px] h-[320px] rounded-full blur-3xl opacity-20 pointer-events-none"
            style={{ backgroundColor: ACCENT }}
          />
          <div
            className="absolute -bottom-40 -right-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-10 pointer-events-none"
            style={{ backgroundColor: ACCENT }}
          />

          <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20 text-center">
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-xs font-semibold mb-5"
              style={{
                borderColor: `${ACCENT}40`,
                backgroundColor: `${ACCENT}14`,
                color: ACCENT,
              }}
            >
              <Sparkles size={14} />
              DartCloud Community
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-4">
              Eine Community.
              <br className="hidden sm:block" />{' '}
              <span
                className="text-transparent bg-clip-text"
                style={{
                  backgroundImage: `linear-gradient(90deg, ${ACCENT}, #7de8ea)`,
                }}
              >
                Ein Sport.
              </span>
            </h1>

            <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
              Entdecke die Profile anderer Spieler, lass dich von ihren Dart-Setups
              inspirieren und diskutiere mit der Community im Forum.
            </p>

            {/* Stat-Pills (nur echte API-Werte, sonst ausblenden) */}
            {(totalProfiles !== null || totalSetups !== null || totalTopics !== null) && (
              <div className="flex flex-wrap justify-center gap-2.5 md:gap-3">
                {totalProfiles !== null && (
                  <StatPill
                    icon={<Users size={15} />}
                    value={totalProfiles}
                    label="Spielerprofile"
                  />
                )}
                {totalSetups !== null && (
                  <StatPill
                    icon={<Gamepad2 size={15} />}
                    value={totalSetups}
                    label="Dart Setups"
                  />
                )}
                {totalTopics !== null && (
                  <StatPill
                    icon={<MessagesSquare size={15} />}
                    value={totalTopics}
                    label="Forum-Themen"
                  />
                )}
              </div>
            )}
          </div>
        </section>

        {/* ============ Content ============ */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16 space-y-14 md:space-y-20">
          {/* Profile Section */}
          <section>
            <SectionHeader
              icon={<User size={22} color={ACCENT} />}
              title="Neue Spielerprofile"
              subtitle={
                totalProfiles !== null
                  ? `${totalProfiles} Spieler haben ein öffentliches Profil`
                  : 'Öffentliche Profile aus der Community'
              }
            />

            {loadingProfiles ? (
              <ProfileSkeletonGrid />
            ) : profiles.length === 0 ? (
              <EmptyBlock text="Noch keine öffentlichen Profile vorhanden" />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {profiles.map((p) => (
                  <ProfileCard key={p.id || p.username} profile={p} />
                ))}
              </div>
            )}
          </section>

          {/* Setups Section */}
          <section>
            <SectionHeader
              icon={<Gamepad2 size={22} color={ACCENT} />}
              title="Neue Dart Setups"
              subtitle={
                totalSetups !== null
                  ? `${totalSetups} Setups von der Community`
                  : 'Setups von der Community'
              }
            />

            {loadingSetups ? (
              <SetupSkeletonGrid />
            ) : setups.length === 0 ? (
              <EmptyBlock text="Noch keine Dart Setups vorhanden" />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {setups.map((s) => (
                  <SetupCard key={s.id} setup={s} />
                ))}
              </div>
            )}
          </section>

          {/* Forum-Teaser Section (bei Fehler/leer komplett ausgeblendet) */}
          {(loadingTopics || forumTopics.length > 0) && (
            <section>
              <SectionHeader
                icon={<Flame size={22} color={ACCENT} />}
                title="Aktuell im Forum"
                subtitle="Die heißesten Diskussionen der Community"
                action={
                  <Button
                    as={RouterLink}
                    to="/forum"
                    size="sm"
                    variant="flat"
                    endContent={<ArrowRight size={16} />}
                    className="hidden sm:inline-flex font-semibold"
                    style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}
                  >
                    Zum Forum
                  </Button>
                }
              />

              {loadingTopics ? (
                <ForumSkeletonList />
              ) : (
                <>
                  <div className="space-y-2.5">
                    {forumTopics.slice(0, 5).map((topic) => (
                      <ForumTopicRow key={topic.id || topic._id || topic.slug} topic={topic} />
                    ))}
                  </div>
                  <div className="mt-4 sm:hidden">
                    <Button
                      as={RouterLink}
                      to="/forum"
                      size="sm"
                      variant="flat"
                      fullWidth
                      endContent={<ArrowRight size={16} />}
                      className="font-semibold"
                      style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}
                    >
                      Zum Forum
                    </Button>
                  </div>
                </>
              )}
            </section>
          )}

          {/* Community-CTA-Banner */}
          <section>
            <div className="relative overflow-hidden rounded-2xl border border-zinc-800">
              <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 to-zinc-950" />
              <div
                className="absolute -top-24 -left-16 w-[360px] h-[360px] rounded-full blur-3xl opacity-15 pointer-events-none"
                style={{ backgroundColor: ACCENT }}
              />
              <div
                className="absolute -bottom-28 -right-16 w-[300px] h-[300px] rounded-full blur-3xl opacity-10 pointer-events-none"
                style={{ backgroundColor: ACCENT }}
              />

              <div className="relative px-6 md:px-12 py-10 md:py-14 text-center">
                <div
                  className="inline-flex p-3 rounded-2xl mb-5"
                  style={{ backgroundColor: `${ACCENT}1A` }}
                >
                  <Users size={28} color={ACCENT} />
                </div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">
                  Werde Teil der Community
                </h2>
                <p className="text-gray-400 max-w-xl mx-auto leading-relaxed mb-7">
                  {isAuthenticated
                    ? 'Aktiviere dein öffentliches Profil, zeige der Community dein Dart-Setup und diskutiere im Forum mit.'
                    : 'Erstelle dein kostenloses Profil, präsentiere dein Dart-Setup und tausche dich mit anderen Spielern aus.'}
                </p>

                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  {isAuthenticated ? (
                    <>
                      <Button
                        as={RouterLink}
                        to="/profil"
                        className="font-semibold text-zinc-950"
                        style={{ backgroundColor: ACCENT }}
                        startContent={<User size={18} />}
                      >
                        Öffentliches Profil aktivieren
                      </Button>
                      <Button
                        as={RouterLink}
                        to="/forum"
                        variant="bordered"
                        className="font-semibold text-white border-zinc-700"
                        startContent={<MessagesSquare size={18} />}
                      >
                        Zum Forum
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        as={RouterLink}
                        to="/register"
                        className="font-semibold text-zinc-950"
                        style={{ backgroundColor: ACCENT }}
                        startContent={<UserPlus size={18} />}
                      >
                        Jetzt kostenlos registrieren
                      </Button>
                      <Button
                        as={RouterLink}
                        to="/login"
                        variant="bordered"
                        className="font-semibold text-white border-zinc-700"
                        startContent={<LogIn size={18} />}
                      >
                        Anmelden
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

// ============================================
// Sub-Components
// ============================================

const StatPill: React.FC<{
  icon: React.ReactNode;
  value: number;
  label: string;
}> = ({ icon, value, label }) => (
  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900/80 border border-zinc-800 backdrop-blur-sm">
    <span style={{ color: ACCENT }}>{icon}</span>
    <span className="text-sm font-bold text-white">
      {value.toLocaleString('de-DE')}
    </span>
    <span className="text-sm text-gray-400">{label}</span>
  </div>
);

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  action?: React.ReactNode;
}> = ({ icon, title, subtitle, action }) => (
  <div className="flex items-center gap-3 mb-6">
    <div
      className="p-2.5 rounded-xl border flex-shrink-0"
      style={{ backgroundColor: `${ACCENT}14`, borderColor: `${ACCENT}33` }}
    >
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <h2 className="text-lg md:text-xl font-bold text-white truncate">{title}</h2>
      <p className="text-xs md:text-sm text-gray-500 truncate">{subtitle}</p>
    </div>
    {action}
  </div>
);

const EmptyBlock: React.FC<{ text: string }> = ({ text }) => (
  <div className="text-center py-16 text-gray-500 rounded-2xl border border-dashed border-zinc-800">
    {text}
  </div>
);

// ---------- Skeletons ----------

const ProfileSkeletonGrid: React.FC = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
    {Array.from({ length: 8 }).map((_, i) => (
      <Card key={i} className="bg-zinc-900 border border-zinc-800" shadow="sm">
        <CardBody className="p-0">
          <Skeleton className="h-16 w-full rounded-none bg-zinc-800" />
          <div className="flex justify-center -mt-7 mb-1">
            <Skeleton className="w-14 h-14 rounded-full bg-zinc-800 ring-4 ring-zinc-900" />
          </div>
          <div className="px-4 pb-4 flex flex-col items-center gap-2">
            <Skeleton className="h-3.5 w-24 rounded-md bg-zinc-800" />
            <Skeleton className="h-2.5 w-16 rounded-md bg-zinc-800" />
          </div>
        </CardBody>
      </Card>
    ))}
  </div>
);

const SetupSkeletonGrid: React.FC = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
    {Array.from({ length: 8 }).map((_, i) => (
      <Card key={i} className="bg-zinc-900 border border-zinc-800" shadow="sm">
        <CardBody className="p-0">
          <Skeleton className="aspect-square w-full rounded-none bg-zinc-800" />
          <div className="p-3 space-y-2">
            <Skeleton className="h-3.5 w-3/4 rounded-md bg-zinc-800" />
            <Skeleton className="h-2.5 w-1/2 rounded-md bg-zinc-800" />
          </div>
        </CardBody>
      </Card>
    ))}
  </div>
);

const ForumSkeletonList: React.FC = () => (
  <div className="space-y-2.5">
    {Array.from({ length: 4 }).map((_, i) => (
      <Card key={i} className="bg-zinc-900 border border-zinc-800" shadow="sm">
        <CardBody className="flex flex-row items-center gap-4 px-4 py-3.5">
          <Skeleton className="w-10 h-10 rounded-xl bg-zinc-800 flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-2/3 rounded-md bg-zinc-800" />
            <Skeleton className="h-2.5 w-1/3 rounded-md bg-zinc-800" />
          </div>
        </CardBody>
      </Card>
    ))}
  </div>
);

// ---------- Cards ----------

const ProfileCard: React.FC<{ profile: ProfilePreview }> = ({ profile }) => {
  const avatarUrl = communityService.buildThumbnailUrl(profile.image, 100);
  const headerUrl = communityService.buildThumbnailUrl(profile.headerImage, 400);
  const memberSince = communityService.formatMemberSince(profile.memberSince ?? undefined);

  return (
    <RouterLink
      to={`/spieler/${encodeURIComponent(profile.username)}`}
      className="block group"
      aria-label={`Profil von ${profile.username} ansehen`}
    >
      <Card
        className="bg-zinc-900 border border-zinc-800 overflow-hidden w-full transition-all duration-300 group-hover:border-[#3fced1]/60 group-hover:-translate-y-1 group-hover:shadow-[0_12px_32px_-8px_rgba(63,206,209,0.25)]"
        shadow="sm"
      >
        <CardBody className="p-0">
          {/* Header (Gradient + optionales Header-Bild) */}
          <div className="relative h-16 w-full overflow-hidden">
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(135deg, ${ACCENT}4D 0%, #0078B44D 100%)`,
              }}
            />
            {headerUrl && (
              <img
                src={headerUrl}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover relative transition-transform duration-500 group-hover:scale-105"
                onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
              />
            )}
            <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-zinc-900/70 to-transparent" />
          </div>

          {/* Avatar (overlapping) */}
          <div className="flex justify-center -mt-7 mb-1.5">
            <div className="rounded-full p-[3px] bg-zinc-900 transition-colors duration-300 group-hover:bg-[#3fced1]/30">
              <div className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={profile.username}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <User size={28} className="text-gray-600" />
                )}
              </div>
            </div>
          </div>

          {/* Username + Member-Since */}
          <div className="px-2 pb-4 text-center">
            <p className="text-sm font-bold text-white truncate transition-colors group-hover:text-[#3fced1]">
              {profile.username}
            </p>
            {memberSince && (
              <p className="text-[10px] text-gray-500 mt-0.5">Seit {memberSince}</p>
            )}
          </div>
        </CardBody>
      </Card>
    </RouterLink>
  );
};

const SetupCard: React.FC<{ setup: PublicDartSetup }> = ({ setup }) => {
  const imgUrl = communityService.buildThumbnailUrl(setup.image, 400);
  const userAvatar = communityService.buildThumbnailUrl(setup.user?.image, 100);

  return (
    <RouterLink
      to={`/setup/${encodeURIComponent(setup.id)}`}
      className="block group"
      aria-label={`Setup ${setup.title} ansehen`}
    >
      <Card
        className="bg-zinc-900 border border-zinc-800 overflow-hidden w-full transition-all duration-300 group-hover:border-[#3fced1]/60 group-hover:-translate-y-1 group-hover:shadow-[0_12px_32px_-8px_rgba(63,206,209,0.25)]"
        shadow="sm"
      >
        <CardBody className="p-0 flex flex-col">
          <div className="aspect-square w-full bg-zinc-800 flex items-center justify-center overflow-hidden">
            {imgUrl ? (
              <img
                src={imgUrl}
                alt={setup.title}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <ImageOff size={40} className="text-gray-600" />
            )}
          </div>

          <div className="p-3">
            <p className="text-sm font-bold text-white truncate transition-colors group-hover:text-[#3fced1]">
              {setup.title}
            </p>

            {setup.user && (
              <div className="mt-1.5 flex items-center gap-1.5">
                <div className="w-4 h-4 rounded-full bg-zinc-700 overflow-hidden flex items-center justify-center flex-shrink-0">
                  {userAvatar ? (
                    <img
                      src={userAvatar}
                      alt={setup.user.username}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <User size={10} className="text-gray-500" />
                  )}
                </div>
                <span
                  className="text-[11px] font-medium truncate"
                  style={{ color: ACCENT }}
                >
                  {setup.user.username}
                </span>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </RouterLink>
  );
};

const ForumTopicRow: React.FC<{ topic: ForumTopic }> = ({ topic }) => {
  const title = (topic.title || '').trim();
  if (!title) return null;

  const relativeTime = forumService.formatRelativeTime(
    topic.lastPostTime ?? topic.createdAt ?? null
  );

  const inner = (
    <Card
      className="bg-zinc-900 border border-zinc-800 w-full transition-all duration-300 group-hover:border-[#3fced1]/60 group-hover:shadow-[0_8px_24px_-8px_rgba(63,206,209,0.2)]"
      shadow="sm"
    >
      <CardBody className="flex flex-row items-center gap-3 md:gap-4 px-4 py-3.5">
        {/* Score-Badge */}
        <div
          className="flex flex-col items-center justify-center w-10 h-10 rounded-xl flex-shrink-0"
          style={{ backgroundColor: `${ACCENT}14` }}
        >
          <ArrowBigUp size={14} color={ACCENT} />
          <span className="text-[11px] font-bold leading-none" style={{ color: ACCENT }}>
            {topic.score ?? 0}
          </span>
        </div>

        {/* Titel + Meta */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate transition-colors group-hover:text-[#3fced1]">
            {title}
          </p>
          <div className="mt-1 flex items-center gap-2.5 flex-wrap">
            {topic.categoryName && (
              <Chip
                size="sm"
                variant="flat"
                classNames={{
                  base: 'h-5 px-1.5 bg-zinc-800 border border-zinc-700',
                  content: 'text-[10px] text-gray-300 px-1',
                }}
              >
                {topic.categoryName}
              </Chip>
            )}
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
              <MessageCircle size={11} /> {topic.postCount ?? 0} Antworten
            </span>
            {relativeTime && (
              <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                <Clock size={11} /> {relativeTime}
              </span>
            )}
          </div>
        </div>

        <ArrowRight
          size={16}
          className="text-gray-600 flex-shrink-0 transition-all duration-300 group-hover:text-[#3fced1] group-hover:translate-x-0.5"
        />
      </CardBody>
    </Card>
  );

  // Ohne Slug keine Topic-Detail-URL → Fallback-Link aufs Forum
  return (
    <RouterLink
      to={topic.slug ? `/forum/topic/${encodeURIComponent(topic.slug)}` : '/forum'}
      className="block group"
      aria-label={`Forum-Thema ${title} ansehen`}
    >
      {inner}
    </RouterLink>
  );
};

export default CommunityPage;
